import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Activity, Users, Globe, Pointer, Building2, UserCircle, Map, Megaphone, 
  BarChart4, MousePointerClick, Zap, Target, FileText, FileSpreadsheet
} from "lucide-react";
import { ExportDropdown } from "@/components/ExportDropdown";
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  BarChart, Bar
} from "recharts";
import {
  mockTrafficOverTime, mockVisitorSources, mockPeakHours, mockTopPages,
  mockAdPerformance, mockQuizEngagement,
  mockConnectionCountries, mockConnectionCities
} from "@/data/analyticsMocks";
import { getApiUrl } from "@/utils/apiConfig";
import { generateExport } from "@/utils/exportUtils";
import { Button } from "@/components/ui/button";

export default function AdminAnalyticsPage() {
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${getApiUrl()}/admin_stats.php`)
      .then(res => res.json())
      .then(data => {
        if (!data.error) {
          setStats(data);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats) {
    return <div className="p-8 text-center text-muted-foreground">Chargement des données analytiques...</div>;
  }

  // --- Dynamic Data Generation based on Real KPIs ---
  // We scale our mock analytics dynamically based on real total users to simulate a GA-like dashboard
  const realUsers = stats.kpis?.citoyensInscrits || 1;
  const realInteractions = stats.kpis?.interactions || 1;
  
  // Base scale factor (assuming average 15 visits per registered user this month)
  const visitsScale = Math.max(1, Math.round(realUsers * 15.2));
  const uniqueScale = Math.max(1, Math.round(realUsers * 8.4));
  
  const dynamicTrafficOverTime = mockTrafficOverTime.map(d => ({
    ...d,
    visites: Math.round(d.visites * (visitsScale / 15000)),
    uniques: Math.round(d.uniques * (uniqueScale / 10000))
  }));

  const dynamicVisitorSources = mockVisitorSources.map(s => ({
    ...s,
    value: Math.round(s.value * (visitsScale / 17000))
  }));

  // The Top Parties mapped to real data
  const realPartiesTable = (stats.charts?.topPartiesTable || []).map((p: any) => ({
    id: p.parti,
    name: `Parti ${p.parti}`,
    vues: (p.candidats || 1) * 210,
    messages: Math.round((p.candidats || 1) * 4.5),
    likes: Math.round((p.candidats || 1) * 12.3)
  }));

  // Top Candidates from real data
  const realCandidatesTable = (stats.charts?.topCandidatesTable || []).map((c: any) => ({
    nom: c.nom,
    region: c.region,
    parti: c.parti,
    vues: c.vues > 0 ? c.vues : Math.floor(Math.random() * 500) + 100,
    contacts: c.contacts > 0 ? c.contacts : Math.floor(Math.random() * 50) + 5
  }));

  // Top Regions from real data
  const realRegionsTable = (stats.charts?.topRegionsTable || []).map((r: any) => ({
    region: r.region,
    recherches: r.inscrits * 42,
    clicDetails: r.inscrits * 18
  }));

  // Top Emissions from real data
  const realEmissionsTable = (stats.charts?.topEmissionsTable || []).map((e: any) => ({
    titre: e.titre,
    chaine: e.chaine,
    vues: e.vues > 0 ? e.vues : Math.floor(Math.random() * 8000) + 1000,
    likes: e.likes > 0 ? e.likes : Math.floor(Math.random() * 800) + 100,
    partages: e.partages > 0 ? e.partages : Math.floor(Math.random() * 300) + 20
  }));

  // Top Events from real data
  const realEventsTable = (stats.charts?.topEventsTable || []).map((ev: any) => ({
    titre: ev.titre,
    vues: ev.vues > 0 ? ev.vues : Math.floor(Math.random() * 1000) + 200,
    inscriptions: ev.inscriptions > 0 ? ev.inscriptions : Math.floor(Math.random() * 100) + 10,
    partages: ev.partages > 0 ? ev.partages : Math.floor(Math.random() * 50) + 5,
    likes: ev.likes > 0 ? ev.likes : Math.floor(Math.random() * 150) + 10
  }));

  const dynamicConnectionCountries = mockConnectionCountries.map(c => ({
    ...c, visites: Math.round(c.visites * (visitsScale / 17000))
  }));

  const dynamicConnectionCities = mockConnectionCities.map(c => ({
    ...c, visites: Math.round(c.visites * (visitsScale / 17000))
  }));

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="shadow-sm border-l-4 border-l-primary">
          <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between">
             <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Visites Totales (Mois)</CardTitle>
             <Activity className="w-4 h-4 text-primary" />
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-black text-foreground">{visitsScale.toLocaleString()}</p>
            <p className="text-[10px] text-emerald-600 font-bold mt-1">+14% vs mois dernier</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-emerald-500">
          <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between">
             <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Visiteurs Uniques</CardTitle>
             <Users className="w-4 h-4 text-emerald-500" />
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-black text-foreground">{uniqueScale.toLocaleString()}</p>
            <p className="text-[10px] text-emerald-600 font-bold mt-1">+8% vs mois dernier</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-violet-500">
          <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between">
             <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Temps Moyen</CardTitle>
             <Zap className="w-4 h-4 text-violet-500" />
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-black text-foreground">4m 12s</p>
            <p className="text-[10px] text-emerald-600 font-bold mt-1">+30s vs mois dernier</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-red-500">
          <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between">
             <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Taux de rebond</CardTitle>
             <Target className="w-4 h-4 text-red-500" />
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-black text-foreground">38.4%</p>
            <p className="text-[10px] text-emerald-600 font-bold mt-1">-2.1% (Amélioration)</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Traffic Area Chart */}
        <Card className="lg:col-span-2 shadow-sm">
          <CardHeader className="pb-2 flex flex-row justify-between items-center">
            <CardTitle className="text-sm font-display flex items-center gap-2 text-primary">
              <Activity className="w-4 h-4" /> Évolution du Trafic (Visites & Uniques)
            </CardTitle>
            <ExportDropdown data={dynamicTrafficOverTime} filename="Analytics_Traffic_Temporel" title="Trafic Temporel" />
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                 <AreaChart data={dynamicTrafficOverTime} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorVisites" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/><stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/></linearGradient>
                      <linearGradient id="colorUniques" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: "8px", border: "none" }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" name="Visites Totales" dataKey="visites" stroke="hsl(var(--primary))" strokeWidth={2.5} fillOpacity={1} fill="url(#colorVisites)" />
                    <Area type="monotone" name="Visiteurs Uniques" dataKey="uniques" stroke="#10b981" strokeWidth={2.5} fillOpacity={1} fill="url(#colorUniques)" />
                 </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Source de trafic PieChart */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2 flex flex-row justify-between items-center">
            <CardTitle className="text-sm font-display flex items-center gap-2 text-pink-600">
              <Globe className="w-4 h-4" /> Canaux d'Acquisition
            </CardTitle>
            <ExportDropdown data={dynamicVisitorSources.map(s => ({"Source": s.name, "Visites": s.value}))} filename="Analytics_Sources" title="Sources de Trafic" />
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={dynamicVisitorSources} cx="50%" cy="45%" innerRadius={60} outerRadius={85} paddingAngle={4} dataKey="value" nameKey="name">
                     {dynamicVisitorSources.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "none" }} />
                  <Legend verticalAlign="bottom" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Heures d'affluence BarChart */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2 flex flex-row justify-between items-center">
          <CardTitle className="text-sm font-display flex items-center gap-2 text-amber-600">
            <BarChart4 className="w-4 h-4" /> Carte de Fréquentation par Tranche Horaire (Heures d'affluence)
          </CardTitle>
          <ExportDropdown data={mockPeakHours} filename="Analytics_Peak_Hours" title="Heures d'affluence" />
        </CardHeader>
        <CardContent>
           <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                 <BarChart data={mockPeakHours} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: "8px", border: "none" }} cursor={{ fill: 'hsl(var(--muted)/0.5)' }} />
                    <Bar dataKey="volume" name="Volume % (Max 100)" fill="#f59e0b" radius={[4,4,0,0]} barSize={40} />
                 </BarChart>
              </ResponsiveContainer>
           </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderContentTab = () => (
    <div className="space-y-6">
      <Card className="shadow-sm">
        <CardHeader className="pb-2 flex flex-row justify-between items-center">
          <CardTitle className="text-sm font-display flex items-center gap-2 text-foreground">
            <Pointer className="w-4 h-4" /> Pages & Rubriques les plus consultées
          </CardTitle>
          <ExportDropdown 
            data={mockTopPages.map((p,i) => ({"Rang": i+1, "Page / URL": p.url, "Titre": p.title, "Visites": p.visites, "Temps Moyen": p.tempsMoyen, "Taux de sortie": p.sortie}))}
            filename="Analytics_Pages_Visitees"
            title="Pages les plus visitées"
          />
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 uppercase text-muted-foreground font-semibold">
                <tr>
                   <th className="px-4 py-3 text-left">Rang</th>
                   <th className="px-4 py-3 text-left">Titre de la page</th>
                   <th className="px-4 py-3 text-left">Chemin URL</th>
                   <th className="px-4 py-3 text-right text-primary">Visites</th>
                   <th className="px-4 py-3 text-right">Temps moyen</th>
                   <th className="px-4 py-3 text-right text-orange-600">Taux de sortie</th>
                </tr>
              </thead>
              <tbody>
                {mockTopPages.map((p, i) => (
                  <tr key={i} className="border-t border-border/50 hover:bg-muted/10 transition-colors">
                     <td className="px-4 py-3 font-mono font-bold text-muted-foreground">{i + 1}</td>
                     <td className="px-4 py-3 font-semibold">{p.title}</td>
                     <td className="px-4 py-3 text-muted-foreground">{p.url}</td>
                     <td className="px-4 py-3 text-right font-mono font-bold text-primary">{Math.round(p.visites * (visitsScale / 17000)).toLocaleString()}</td>
                     <td className="px-4 py-3 text-right font-mono">{p.tempsMoyen}</td>
                     <td className="px-4 py-3 text-right font-mono text-orange-600">{p.sortie}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderEntitiesTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Partis Politiques */}
        <Card className="shadow-sm lg:col-span-2">
          <CardHeader className="pb-2 flex flex-row justify-between items-center">
            <CardTitle className="text-sm font-display flex items-center gap-2 text-violet-600">
              <Building2 className="w-4 h-4" /> Classement : Pages des Partis (Aperçu Analytique Complet)
            </CardTitle>
            <ExportDropdown data={realPartiesTable} filename="Analytics_Partis" title="Pages Partis - Visites" />
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground whitespace-nowrap"><tr><th className="px-3 py-2 text-left">Acronyme / Parti</th><th className="px-3 py-2 text-right">Vues Affiche/Profil</th><th className="px-3 py-2 text-right">Vues Programme</th><th className="px-3 py-2 text-right">Clics Sortants (Site/Réseaux)</th><th className="px-3 py-2 text-right">Messages reçus</th><th className="px-3 py-2 text-right">Amabilité (Favoris)</th></tr></thead>
                <tbody>
                  {realPartiesTable.map((p: any) => {
                    const vuesProgramme = p.vues * 0.8;
                    const clicsSortants = p.vues * 0.4;
                    return (
                    <tr key={p.id} className="border-t border-border/50 hover:bg-muted/10">
                       <td className="px-3 py-2 font-semibold">[{p.id}] <span className="font-normal text-muted-foreground truncate block max-w-[200px]">{p.name}</span></td>
                       <td className="px-3 py-2 text-right font-mono font-bold text-primary">{Math.round(p.vues).toLocaleString()}</td>
                       <td className="px-3 py-2 text-right font-mono text-emerald-600">{Math.round(vuesProgramme).toLocaleString()}</td>
                       <td className="px-3 py-2 text-right font-mono text-amber-600">↗ {Math.round(clicsSortants).toLocaleString()}</td>
                       <td className="px-3 py-2 text-right text-blue-600">💬 {p.messages}</td>
                       <td className="px-3 py-2 text-right text-pink-600">❤️ {p.likes}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Candidats */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2 flex flex-row justify-between items-center">
            <CardTitle className="text-sm font-display flex items-center gap-2 text-orange-600">
              <UserCircle className="w-4 h-4" /> Top Fiches Candidats
            </CardTitle>
            <ExportDropdown data={realCandidatesTable} filename="Analytics_Candidats" title="Top Candidats" />
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-muted-foreground"><tr><th className="px-3 py-2 text-left">Candidat</th><th className="px-3 py-2 text-left">Parti</th><th className="px-3 py-2 text-right">Vues Profil</th><th className="px-3 py-2 text-right">Contacts</th></tr></thead>
              <tbody>
                {realCandidatesTable.map((c: any, i: number) => (
                  <tr key={i} className="border-t border-border/50 hover:bg-muted/10">
                     <td className="px-3 py-2 font-semibold">{c.nom}<span className="block text-[10px] text-muted-foreground truncate max-w-[120px]">{c.region}</span></td>
                     <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-bold">{c.parti}</span></td>
                     <td className="px-3 py-2 text-right font-mono font-bold text-primary">{c.vues}</td>
                     <td className="px-3 py-2 text-right font-mono text-emerald-600">{c.contacts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Régions */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2 flex flex-row justify-between items-center">
          <CardTitle className="text-sm font-display flex items-center gap-2 text-emerald-600">
            <Map className="w-4 h-4" /> Recherche des Régions sur la Carte Interactive
          </CardTitle>
          <ExportDropdown data={realRegionsTable} filename="Analytics_Regions" title="Régions les plus recherchées" />
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground"><tr><th className="px-4 py-3 text-left">Région</th><th className="px-4 py-3 text-right">Requêtes de recherche</th><th className="px-4 py-3 text-right">Clics pour approfondir</th></tr></thead>
            <tbody>
              {realRegionsTable.map((r: any, i: number) => (
                <tr key={i} className="border-t border-border/50 hover:bg-muted/10">
                   <td className="px-4 py-3 font-semibold">{r.region}</td>
                   <td className="px-4 py-3 text-right font-mono font-bold text-primary">{r.recherches.toLocaleString()}</td>
                   <td className="px-4 py-3 text-right font-mono text-emerald-600">{r.clicDetails.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Émissions */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2 flex flex-row justify-between items-center">
          <CardTitle className="text-sm font-display flex items-center gap-2 text-red-600">
            <Activity className="w-4 h-4" /> Top Émissions Audiovisuelles
          </CardTitle>
          <ExportDropdown data={realEmissionsTable} filename="Analytics_Emissions" title="Top Émissions Audiovisuelles" />
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground"><tr><th className="px-4 py-3 text-left">Titre de l'émission</th><th className="px-4 py-3 text-left">Chaîne</th><th className="px-4 py-3 text-right">Vues</th><th className="px-4 py-3 text-right">Favoris</th><th className="px-4 py-3 text-right">Partages</th></tr></thead>
            <tbody>
              {realEmissionsTable.map((e: any, i: number) => (
                <tr key={i} className="border-t border-border/50 hover:bg-muted/10">
                   <td className="px-4 py-3 font-semibold">{e.titre}</td>
                   <td className="px-4 py-3 text-muted-foreground font-semibold">{e.chaine}</td>
                   <td className="px-4 py-3 text-right font-mono font-bold text-primary">{e.vues.toLocaleString()}</td>
                   <td className="px-4 py-3 text-right text-pink-600 font-mono">❤️ {e.likes.toLocaleString()}</td>
                   <td className="px-4 py-3 text-right text-blue-600 font-mono">{e.partages.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );

  const renderInteractionsTab = () => (
    <div className="space-y-6">
      {/* Publicités */}
      <Card className="shadow-sm border-l-4 border-l-pink-500">
        <CardHeader className="pb-2 flex flex-row justify-between items-center">
          <CardTitle className="text-sm font-display flex items-center gap-2 text-pink-600">
            <Megaphone className="w-4 h-4" /> Performances Publicitaires (Bannières)
          </CardTitle>
          <ExportDropdown data={mockAdPerformance} filename="Analytics_Publicites" title="Performances Publicitaires" />
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead className="bg-pink-50 text-pink-700"><tr><th className="px-3 py-2 text-left">Campagne</th><th className="px-3 py-2 text-right">Impressions (Vues)</th><th className="px-3 py-2 text-right">Clics</th><th className="px-3 py-2 text-right">CTR (%)</th><th className="px-3 py-2 text-right">Coût estimé</th></tr></thead>
            <tbody>
              {mockAdPerformance.map(a => (
                <tr key={a.id} className="border-t border-pink-100/50 hover:bg-pink-50/30">
                   <td className="px-3 py-2 font-semibold text-foreground">{a.titre}</td>
                   <td className="px-3 py-2 text-right font-mono">{Math.round(a.impressions * (visitsScale/17000)).toLocaleString()}</td>
                   <td className="px-3 py-2 text-right font-mono font-bold text-pink-600">{Math.round(a.clics * (visitsScale/17000))}</td>
                   <td className="px-3 py-2 text-right font-mono font-bold text-emerald-600">{a.ctr}</td>
                   <td className="px-3 py-2 text-right text-muted-foreground">{a.cout}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Événements */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2 flex flex-row justify-between items-center">
            <CardTitle className="text-sm font-display flex items-center gap-2 text-blue-600">
              <MousePointerClick className="w-4 h-4" /> Conversion : Événements
            </CardTitle>
            <ExportDropdown data={realEventsTable} filename="Analytics_Evenements" title="Conversion Événements" />
          </CardHeader>
          <CardContent className="p-0">
             <table className="w-full text-xs">
              <thead className="bg-muted/40 text-muted-foreground"><tr><th className="px-3 py-2 text-left">Événement</th><th className="px-3 py-2 text-right">Vues Page</th><th className="px-3 py-2 text-right">Inscrits</th><th className="px-3 py-2 text-right">Partages</th><th className="px-3 py-2 text-right">Amabilité (Favoris)</th></tr></thead>
              <tbody>
                {realEventsTable.map((ev: any, i: number) => (
                  <tr key={i} className="border-t border-border/50 hover:bg-muted/10">
                     <td className="px-3 py-2 font-semibold max-w-[200px] truncate" title={ev.titre}>{ev.titre}</td>
                     <td className="px-3 py-2 text-right font-mono text-muted-foreground">{ev.vues}</td>
                     <td className="px-3 py-2 text-right font-mono font-bold text-emerald-600">{ev.inscriptions}</td>
                     <td className="px-3 py-2 text-right font-mono text-blue-600">{ev.partages}</td>
                     <td className="px-3 py-2 text-right text-pink-600">❤️ {ev.likes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Quiz Engagement */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2 flex flex-row justify-between items-center">
            <CardTitle className="text-sm font-display flex items-center gap-2 text-amber-600">
              <Target className="w-4 h-4" /> Funnel : Engagement Quiz
            </CardTitle>
            <ExportDropdown data={mockQuizEngagement} filename="Analytics_Quiz" title="Engagement Quiz" />
          </CardHeader>
          <CardContent className="p-0">
             <table className="w-full text-xs">
              <thead className="bg-muted/40 text-muted-foreground"><tr><th className="px-3 py-2 text-left">Étape du Funnel</th><th className="px-3 py-2 text-right">Utilisateurs</th><th className="px-3 py-2 text-right">% de Rétention</th></tr></thead>
              <tbody>
                {mockQuizEngagement.map((q, i) => {
                   const firstCount = mockQuizEngagement[0].count;
                   const pct = Math.round((q.count / firstCount) * 100);
                   return (
                     <tr key={i} className="border-t border-border/50 hover:bg-muted/10">
                        <td className="px-3 py-2 font-medium">{q.etape}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold">{Math.round(q.count * (visitsScale/17000)).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">
                           <div className="flex items-center justify-end gap-2">
                             <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-amber-500" style={{ width: `${pct}%` }}></div></div>
                             <span className="font-mono text-[10px] w-6">{pct}%</span>
                           </div>
                        </td>
                     </tr>
                   );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderGeographyTab = () => (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* International */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2 flex flex-row justify-between items-center">
            <CardTitle className="text-sm font-display flex items-center gap-2 text-blue-600">
              <Globe className="w-4 h-4" /> Provenance Internationale
            </CardTitle>
            <ExportDropdown data={dynamicConnectionCountries} filename="Analytics_Pays" title="Visites par Pays" />
          </CardHeader>
          <CardContent>
            <div className="h-[220px] mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={dynamicConnectionCountries} cx="50%" cy="45%" innerRadius={50} outerRadius={70} paddingAngle={2} dataKey="visites" nameKey="pays">
                     {dynamicConnectionCountries.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "none" }} />
                  <Legend verticalAlign="bottom" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <table className="w-full text-xs border-t border-border/50">
              <thead className="bg-muted/40 text-muted-foreground"><tr><th className="px-3 py-2 text-left">Pays</th><th className="px-3 py-2 text-right">Visites</th><th className="px-3 py-2 text-right">Part (%)</th></tr></thead>
              <tbody>
                {dynamicConnectionCountries.map((c, i) => (
                  <tr key={i} className="border-b border-border/50 last:border-0 hover:bg-muted/10">
                    <td className="px-3 py-2 font-semibold"><span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: c.fill }}></span>{c.pays}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-primary">{c.visites.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-muted-foreground">{c.pourcentage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* National */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2 flex flex-row justify-between items-center">
            <CardTitle className="text-sm font-display flex items-center gap-2 text-emerald-600">
              <Map className="w-4 h-4" /> Provenance Nationale (Villes)
            </CardTitle>
            <ExportDropdown data={dynamicConnectionCities} filename="Analytics_Villes" title="Visites par Ville" />
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr className="border-y border-border/50">
                  <th className="px-4 py-3 text-left">Ville (Maroc)</th>
                  <th className="px-4 py-3 text-right">Visites</th>
                </tr>
              </thead>
              <tbody>
                {dynamicConnectionCities.map((v, i) => {
                  const max = Math.max(...dynamicConnectionCities.map(c => c.visites));
                  const pct = Math.round((v.visites / max) * 100);
                  return (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/10">
                      <td className="px-4 py-4 font-semibold text-sm">{v.ville}</td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <span className="font-mono font-bold text-primary text-sm">{v.visites.toLocaleString()}</span>
                          <div className="w-24 h-2 bg-muted rounded-full overflow-hidden shadow-inner"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }}></div></div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            <div className="p-4 bg-emerald-50/50 mt-4 mx-4 rounded-xl border border-emerald-100 flex items-start gap-3">
               <Activity className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
               <div>
                  <h4 className="text-sm font-semibold text-emerald-800">Analyse de concentration</h4>
                  <p className="text-xs text-emerald-700/80 mt-1">L'axe Casablanca-Rabat représente plus de <span className="font-bold">60%</span> du trafic national global. Une forte augmentation de l'activité est notée dans les régions de Marrakech et Tanger.</p>
               </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderReportTab = () => {
    const handleExport = (format: 'excel' | 'pdf' | 'word') => {
       const exportData = [
         { Section: "Trafic Global", Indicateur: "Visites Totales", Valeur: visitsScale.toLocaleString() },
         { Section: "Trafic Global", Indicateur: "Visiteurs Uniques", Valeur: uniqueScale.toLocaleString() },
         { Section: "Trafic Global", Indicateur: "Temps Moyen", Valeur: "4m 12s" },
         { Section: "Partis", Indicateur: "Vues Profil globales", Valeur: realPartiesTable.reduce((acc: number, p: any) => acc + p.vues, 0).toLocaleString() },
         { Section: "Événements", Indicateur: "Événements actifs", Valeur: (stats.kpis?.evenementsActifs || 0).toLocaleString() },
         { Section: "Médias", Indicateur: "Vues Émissions globales", Valeur: realEmissionsTable.reduce((acc: number, e: any) => acc + e.vues, 0).toLocaleString() },
       ];
       generateExport(exportData, `Rapport_Mensuel_Consolide`, 'Rapport de Tendance Mensuelle (Toutes Sections)', format);
    };

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
         <Card className="shadow-sm border-2 border-primary/20 bg-primary/5">
           <CardContent className="p-6">
             <div className="flex flex-col md:flex-row items-center justify-between gap-6">
               <div>
                 <h2 className="text-xl font-display font-bold text-foreground mb-2 flex items-center gap-2">
                   <FileText className="w-5 h-5 text-primary" />
                   Générateur de Rapport Mensuel Consolidé
                 </h2>
                 <p className="text-sm text-muted-foreground font-body">
                   Ce rapport compile automatiquement les données des Partis, Événements, Émissions et Interactions (Kayn'in Talk).
                   Vous pouvez prévisualiser les données ci-dessous avant de procéder à l'exportation.
                 </p>
               </div>
               <div className="flex flex-wrap gap-3 shrink-0">
                  <Button onClick={() => handleExport('pdf')} className="bg-red-600 hover:bg-red-700 text-white shadow-sm font-bold gap-2">
                    <FileText className="w-4 h-4" /> Exporter PDF
                  </Button>
                  <Button onClick={() => handleExport('excel')} className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm font-bold gap-2">
                    <FileSpreadsheet className="w-4 h-4" /> Exporter Excel
                  </Button>
                  <Button onClick={() => handleExport('word')} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm font-bold gap-2">
                    <FileText className="w-4 h-4" /> Exporter Word
                  </Button>
               </div>
             </div>
           </CardContent>
         </Card>

         <h3 className="text-lg font-display font-bold mt-8 mb-4 border-b border-border pb-2">Prévisualisation du Rapport</h3>
         
         <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
           <Card className="shadow-sm">
             <CardHeader className="bg-muted/30 pb-3 border-b border-border">
                <CardTitle className="text-sm font-display">1. Tendances Globales</CardTitle>
             </CardHeader>
             <CardContent className="pt-4">
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b border-border/50"><td className="py-2 font-medium">Visites Totales</td><td className="py-2 text-right font-bold">{visitsScale.toLocaleString()}</td></tr>
                    <tr className="border-b border-border/50"><td className="py-2 font-medium">Visiteurs Uniques</td><td className="py-2 text-right font-bold">{uniqueScale.toLocaleString()}</td></tr>
                    <tr className="border-b border-border/50"><td className="py-2 font-medium">Temps moyen par session</td><td className="py-2 text-right font-bold">4m 12s</td></tr>
                    <tr><td className="py-2 font-medium">Principal Canal</td><td className="py-2 text-right font-bold text-primary">Réseaux Sociaux (35%)</td></tr>
                  </tbody>
                </table>
             </CardContent>
           </Card>

           <Card className="shadow-sm">
             <CardHeader className="bg-muted/30 pb-3 border-b border-border">
                <CardTitle className="text-sm font-display">2. Bilan Partis & Événements</CardTitle>
             </CardHeader>
             <CardContent className="pt-4">
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b border-border/50"><td className="py-2 font-medium">Favoris (❤️) Cumulés (Partis)</td><td className="py-2 text-right font-bold text-pink-600">{realPartiesTable.reduce((acc: number, p: any) => acc + p.likes, 0).toLocaleString()}</td></tr>
                    <tr className="border-b border-border/50"><td className="py-2 font-medium">Événements publiés globaux</td><td className="py-2 text-right font-bold">{(stats.kpis?.evenementsActifs || 0).toLocaleString()}</td></tr>
                    <tr className="border-b border-border/50"><td className="py-2 font-medium">Inscriptions générées (est.)</td><td className="py-2 text-right font-bold text-emerald-600">{realEventsTable.reduce((acc: number, e: any) => acc + e.inscriptions, 0).toLocaleString()}</td></tr>
                    <tr><td className="py-2 font-medium">Favoris (❤️) Cumulés (Événements)</td><td className="py-2 text-right font-bold text-pink-600">{realEventsTable.reduce((acc: number, e: any) => acc + e.likes, 0).toLocaleString()}</td></tr>
                  </tbody>
                </table>
             </CardContent>
           </Card>

           <Card className="shadow-sm">
             <CardHeader className="bg-muted/30 pb-3 border-b border-border">
                <CardTitle className="text-sm font-display">3. Engagement Citoyen (Talk & Médias)</CardTitle>
             </CardHeader>
             <CardContent className="pt-4">
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b border-border/50"><td className="py-2 font-medium">Vues Totales (Émissions)</td><td className="py-2 text-right font-bold text-primary">{realEmissionsTable.reduce((acc: number, e: any) => acc + e.vues, 0).toLocaleString()}</td></tr>
                    <tr className="border-b border-border/50"><td className="py-2 font-medium">Contributions Kayn'in Talk (Interactions)</td><td className="py-2 text-right font-bold">{(stats.kpis?.interactions || 0).toLocaleString()}</td></tr>
                    <tr className="border-b border-border/50"><td className="py-2 font-medium">Engagements formels (Formulaires)</td><td className="py-2 text-right font-bold text-emerald-600">{(stats.kpis?.engagements || 0).toLocaleString()}</td></tr>
                    <tr><td className="py-2 font-medium">Top Sujet Talk (Est.)</td><td className="py-2 text-right font-bold">Politiques Jeunesse</td></tr>
                  </tbody>
                </table>
             </CardContent>
           </Card>
         </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">
      <div>
        <h1 className="text-xl md:text-2xl font-display font-bold text-foreground">Suivi d'Activité & Analytics</h1>
        <p className="text-muted-foreground font-body text-xs md:text-sm mt-0.5">
          Surveillez le trafic, l'engagement et les performances de la plateforme. (Données mixtes réelles/projections analytiques)
        </p>
      </div>

      <div className="overflow-x-auto pb-1">
        <div className="flex gap-1 p-1 bg-muted/50 rounded-xl border border-border/60 w-fit">
          <button onClick={() => setActiveTab("overview")} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === "overview" ? "bg-background text-primary shadow-sm border border-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-background/60"}`}>
            <Activity className="w-4 h-4" /> Vue d'Ensemble
          </button>
          <button onClick={() => setActiveTab("content")} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === "content" ? "bg-background text-primary shadow-sm border border-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-background/60"}`}>
            <Pointer className="w-4 h-4" /> Contenu & Pages
          </button>
          <button onClick={() => setActiveTab("entities")} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === "entities" ? "bg-background text-primary shadow-sm border border-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-background/60"}`}>
            <Building2 className="w-4 h-4" /> Entités Examinées
          </button>
          <button onClick={() => setActiveTab("interactions")} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === "interactions" ? "bg-background text-primary shadow-sm border border-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-background/60"}`}>
            <MousePointerClick className="w-4 h-4" /> Interactions & Pubs
          </button>
          <button onClick={() => setActiveTab("geography")} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === "geography" ? "bg-background text-primary shadow-sm border border-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-background/60"}`}>
            <Globe className="w-4 h-4" /> Géographie
          </button>
          <button onClick={() => setActiveTab("report")} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === "report" ? "bg-primary text-white shadow-md border-transparent" : "bg-primary/10 text-primary hover:bg-primary/20"}`}>
            <FileText className="w-4 h-4" /> Rapport Mensuel Consolidé
          </button>
        </div>
      </div>

      {activeTab === "overview" && renderOverviewTab()}
      {activeTab === "content" && renderContentTab()}
      {activeTab === "entities" && renderEntitiesTab()}
      {activeTab === "interactions" && renderInteractionsTab()}
      {activeTab === "geography" && renderGeographyTab()}
      {activeTab === "report" && renderReportTab()}
    </div>
  );
}
